<template>
  <div class="forecasting-page">
    <div class="forecasting-content">
      <div class="forecasting-sections">
        <div class="forecasting-section-header">
          <h2 class="forecasting-section-title">Forecasting</h2>
        </div>

      <!-- Stress Scenarios -->
      <div class="f-section">
        <div class="f-section-header">
          <h3 class="f-section-title">Stress Scenarios</h3>
        </div>

        <div v-if="stressLoading" class="f-loading">
          <BaseSpinner />
        </div>

        <div v-else-if="stressError" class="f-empty">
          <i class="las la-exclamation-circle f-empty-icon"></i>
          <p>{{ stressError }}</p>
          <BaseButton
            type="button"
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem' }"
            @click="loadStressScenarios"
          >Retry</BaseButton>
        </div>

        <div v-else-if="!hasStressScenarios" class="f-empty">
          <i class="las la-chart-line f-empty-icon"></i>
          <p>Run automated assessment analysis first to generate stress scenarios.</p>
        </div>

        <template v-else>
          <!-- Business performance + trend inline -->
          <div class="stress-meta">
            <p class="stress-context">{{ stressScenarios.business_performance_context || '—' }}</p>
            <span class="trend-badge" :class="getTrendBadgeClass(stressScenarios.overall_trend)">
              {{ formatTrendLabel(stressScenarios.overall_trend) }}
            </span>
          </div>

          <!-- Scenario cards -->
          <div class="scenario-cards" role="radiogroup" aria-label="Forecasting scenario">
            <article
              v-for="scenario in scenarioCards"
              :key="scenario.key"
              class="scenario-card"
              :class="{ selected: selectedScenario === scenario.key }"
              role="radio"
              :aria-checked="selectedScenario === scenario.key"
              tabindex="0"
              @click="selectScenario(scenario.key)"
              @keydown.enter.prevent="selectScenario(scenario.key)"
              @keydown.space.prevent="selectScenario(scenario.key)"
            >
              <div class="scenario-top">
                <div class="scenario-title-row">
                  <span class="scenario-radio" :class="{ checked: selectedScenario === scenario.key }" />
                  <span class="scenario-title">{{ scenario.label }}</span>
                </div>
                <div class="scenario-input-wrap" @click.stop>
                  <input
                    v-model="editableScenarios[scenario.key].growth_pct"
                    type="number"
                    step="0.01"
                    class="scenario-input"
                    :aria-label="`${scenario.label} growth percentage`"
                  />
                  <span class="scenario-input-suffix">%</span>
                </div>
              </div>
              <p class="scenario-rationale">{{ scenario.rationale || '—' }}</p>
              <p class="scenario-payments" :class="{ invisible: selectedScenario !== scenario.key }">
                Payments: {{ formatGrowthPct(getPaymentsIncreaseForSales(editableScenarios[scenario.key].growth_pct)) }}
                <span class="scenario-payments-note">(0.7 × sales)</span>
              </p>
            </article>
          </div>

          <p class="f-hint">
            Select a scenario — edit the growth % if needed. Payments increase = 0.7 × sales.
          </p>
        </template>
      </div>

      <!-- Cutoff date + generate -->
      <div class="f-section">
        <div class="f-section-header">
          <h3 class="f-section-title">Forecasting Parameters</h3>
        </div>

        <div v-if="loading" class="f-loading">
          <BaseSpinner />
        </div>

        <div v-else-if="error" class="f-empty">
          <i class="las la-exclamation-circle f-empty-icon"></i>
          <p>{{ error }}</p>
          <BaseButton
            type="button"
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem' }"
            @click="loadOrganisationDetails"
          >Retry</BaseButton>
        </div>

        <template v-else>
          <div class="organisation-details-content">
            <div class="form-grid form-grid-single">
              <div class="form-group">
                <label class="form-label">Cutoff Date</label>
                <div class="date-input-wrapper">
                  <v-date-picker
                    v-model="forecastingParams.cutoffDate"
                    :masks="dateMasks"
                    :select-attribute="dateAttrs"
                    :popover="datePickerPopover"
                    is-required
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="form-input date-input"
                        :value="inputValue"
                        v-on="inputEvents"
                        placeholder="Select cutoff date"
                        readonly
                      />
                    </template>
                  </v-date-picker>
                </div>
              </div>
            </div>
          </div>

          <div class="f-actions">
            <BaseButton
              type="button"
              stylevariant="primary"
              :disabled="!isFormValid || generating"
              :loading="generating"
              :cus_style="{ width: 'auto', padding: '0.625rem 1.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
              @click="generateForecastingReport"
            >
              <i v-if="!generating" class="las la-play"></i>
              {{ generating ? 'Generating…' : 'Generate Forecasting Report' }}
            </BaseButton>
          </div>
        </template>
      </div>

      <div v-if="forecastFile" class="f-section">
        <div class="f-section-header">
          <h3 class="f-section-title">Generated Forecasting Report</h3>
        </div>
        <div class="document-card">
          <div class="document-card-content">
            <div class="document-icon-large">
              <i class="las la-file-excel"></i>
            </div>
            <span class="document-name">{{ forecastFile.name }}</span>
            <span class="document-date">
              {{ formatUploadDate(forecastFile.uploaded_at) }}
            </span>
            <span class="status-badge" :class="getDocumentStatusClass(forecastFile.status)">
              <i :class="getDocumentStatusIcon(forecastFile.status)"></i>
            </span>
            <BaseSpinner
              v-if="downloadingReport === getForecastFileId()"
              size="small"
              class="report-btn-spinner"
            />
            <button
              v-else
              type="button"
              class="report-btn"
              :disabled="isReportDisabled(forecastFile)"
              @click.stop="downloadForecastFile(forecastFile)"
              title="Download Report"
            >
              <i class="las la-download"></i>
            </button>
          </div>
        </div>
      </div>

      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';

const SCENARIO_KEYS = ['optimistic', 'base', 'pessimistic'];
const PAYMENTS_FACTOR = 0.7;

function emptyEditableScenarios() {
  return {
    optimistic: { growth_pct: '', rationale: '' },
    base: { growth_pct: '', rationale: '' },
    pessimistic: { growth_pct: '', rationale: '' }
  };
}

export default {
  name: 'ForecastingPage',
  components: {
    BaseButton,
    BaseSpinner
  },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      stressLoading: false,
      stressError: null,
      stressScenarios: null,
      editableScenarios: emptyEditableScenarios(),
      selectedScenario: 'base',
      generating: false,
      downloadingReport: null,
      forecastFile: null,
      organisationDetailsRaw: {},
      candidate: null,
      forecastingParams: {
        cutoffDate: null
      },
      dateMasks: {
        input: 'DD-MMM-YYYY'
      },
      dateAttrs: {
        highlight: {
          start: {
            style: {
              backgroundColor: '#0059BE'
            },
            contentStyle: {
              color: '#ffffff'
            }
          }
        }
      },
      datePickerPopover: {
        visibility: 'click',
        placement: 'bottom-start',
        positionFixed: true
      }
    };
  },
  computed: {
    selectedSalesIncrease() {
      const value = this.editableScenarios?.[this.selectedScenario]?.growth_pct;
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },

    selectedPaymentsIncrease() {
      return this.getPaymentsIncreaseForSales(this.selectedSalesIncrease);
    },

    isFormValid() {
      const hasCutoff = this.forecastingParams.cutoffDate !== null
        && this.forecastingParams.cutoffDate !== undefined
        && this.forecastingParams.cutoffDate !== '';

      return hasCutoff
        && this.hasStressScenarios
        && this.selectedScenario
        && this.selectedSalesIncrease !== null;
    },

    hasStressScenarios() {
      return SCENARIO_KEYS.some((key) => {
        const growth = this.editableScenarios?.[key]?.growth_pct;
        return growth !== '' && growth !== null && growth !== undefined;
      }) || !!(this.stressScenarios?.scenarios);
    },

    scenarioCards() {
      return [
        {
          key: 'optimistic',
          label: 'Optimistic',
          rationale: this.editableScenarios.optimistic.rationale
            || this.stressScenarios?.scenarios?.optimistic?.rationale
        },
        {
          key: 'base',
          label: 'Base',
          rationale: this.editableScenarios.base.rationale
            || this.stressScenarios?.scenarios?.base?.rationale
        },
        {
          key: 'pessimistic',
          label: 'Pessimistic',
          rationale: this.editableScenarios.pessimistic.rationale
            || this.stressScenarios?.scenarios?.pessimistic?.rationale
        }
      ];
    }
  },
  async mounted() {
    await Promise.all([
      this.loadCandidate(),
      this.loadOrganisationDetails(),
      this.loadStressScenarios(),
      this.fetchForecastFile()
    ]);
  },
  methods: {
    async loadCandidate() {
      try {
        this.candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
      } catch (err) {
        console.error('Error loading candidate:', err);
      }
    },

    async loadStressScenarios() {
      this.stressLoading = true;
      this.stressError = null;

      try {
        const response = await ChatbotService.getForecastStressScenarios(this.candidateId);
        this.stressScenarios = response?.forecast_stress_scenarios || null;

        if (!this.stressScenarios?.scenarios) {
          this.stressScenarios = null;
          this.editableScenarios = emptyEditableScenarios();
          return;
        }

        this.initEditableScenarios(this.stressScenarios.scenarios);
      } catch (err) {
        console.error('Error loading stress scenarios:', err);
        this.stressScenarios = null;
        this.editableScenarios = emptyEditableScenarios();
        this.stressError = this.getStressScenarioErrorMessage(err);
      } finally {
        this.stressLoading = false;
      }
    },

    initEditableScenarios(scenarios = {}) {
      const next = emptyEditableScenarios();

      SCENARIO_KEYS.forEach((key) => {
        const source = scenarios[key] || {};
        const growth = source.growth_pct;
        next[key] = {
          growth_pct: growth === null || growth === undefined ? '' : growth,
          rationale: source.rationale || ''
        };
      });

      this.editableScenarios = next;

      if (!SCENARIO_KEYS.includes(this.selectedScenario)) {
        this.selectedScenario = 'base';
      }
    },

    selectScenario(key) {
      if (!SCENARIO_KEYS.includes(key) || this.selectedScenario === key) {
        return;
      }
      this.selectedScenario = key;
    },

    getPaymentsIncreaseForSales(salesIncrease) {
      if (salesIncrease === null || salesIncrease === undefined || salesIncrease === '') {
        return null;
      }
      const numeric = Number(salesIncrease);
      if (!Number.isFinite(numeric)) {
        return null;
      }
      return Number((numeric * PAYMENTS_FACTOR).toFixed(2));
    },

    getStressScenarioErrorMessage(err) {
      const apiMessage = err.response?.data?.message
        || err.response?.data?.detail
        || err.message
        || '';
      const lower = String(apiMessage).toLowerCase();

      if (
        lower.includes('credit model')
        || lower.includes('run model')
        || lower.includes('assessment first')
        || lower.includes('no completed')
      ) {
        return apiMessage || 'Run credit model / assessment first to generate stress scenarios.';
      }

      if (
        lower.includes('scenario')
        || lower.includes('automated assessment')
        || lower.includes('analysis first')
      ) {
        return apiMessage || 'Run automated assessment analysis first to generate stress scenarios.';
      }

      if (err.response?.status === 404 || err.response?.status === 400) {
        return apiMessage
          || 'Run credit model / assessment first to generate stress scenarios.';
      }

      return apiMessage || 'Failed to load stress scenarios.';
    },

    formatGrowthPct(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return '—';
      }

      const sign = numeric > 0 ? '+' : '';
      return `${sign}${numeric.toFixed(1)}%`;
    },

    formatTrendLabel(trend) {
      if (!trend) return 'Unknown';
      return String(trend)
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    },

    getTrendBadgeClass(trend) {
      const normalized = String(trend || '').toLowerCase();
      if (normalized === 'growth') return 'trend-growth';
      if (normalized === 'decline') return 'trend-decline';
      if (normalized === 'stable') return 'trend-stable';
      if (normalized === 'mixed') return 'trend-mixed';
      if (normalized === 'insufficient_history') return 'trend-insufficient';
      return 'trend-unknown';
    },

    async loadOrganisationDetails() {
      this.loading = true;
      this.error = null;

      try {
        const details = await ChatbotService.getOrganisationDetails(this.candidateId);
        this.organisationDetailsRaw = { ...(details || {}) };
        this.populateForecastingParams(this.organisationDetailsRaw);
      } catch (err) {
        console.error('Error loading organisation details:', err);
        this.error = 'Failed to load forecasting parameters';
      } finally {
        this.loading = false;
      }
    },

    populateForecastingParams(details) {
      const params = details.forecasting_parameters
        || details.forecastingParameters
        || {};

      const cutoffValue = params.cutoff_date ?? params.cutoffDate;
      if (cutoffValue) {
        const date = new Date(cutoffValue);
        this.forecastingParams.cutoffDate = Number.isNaN(date.getTime()) ? null : date;
      } else {
        this.forecastingParams.cutoffDate = null;
      }
    },

    formatCutoffDate(value) {
      if (!value) return '';

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    buildForecastingParametersPayload() {
      const salesIncrease = this.selectedSalesIncrease;
      const paymentsIncrease = this.selectedPaymentsIncrease;

      return {
        cutoff_date: this.formatCutoffDate(this.forecastingParams.cutoffDate),
        sales_increase: salesIncrease,
        payments_increase: paymentsIncrease
      };
    },

    async generateForecastingReport() {
      if (!this.isFormValid) {
        this.$bvToast.toast('Select a scenario with a valid growth % and a cutoff date.', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      this.generating = true;

      try {
        const forecastingParameters = this.buildForecastingParametersPayload();
        const payload = {
          ...this.organisationDetailsRaw,
          forecasting_parameters: forecastingParameters
        };

        const saved = await ChatbotService.updateOrganisationDetails(this.candidateId, payload);
        this.organisationDetailsRaw = { ...(saved || payload) };

        const fileBlob = await ChatbotService.forecastPivot(this.candidateId, {
          forecasting_parameters: forecastingParameters
        });
        const fileName = `${this.candidate?.name || 'forecast'} Forecast ${new Date().toISOString().split('T')[0]}.xlsx`;
        const url = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        await this.fetchForecastFile();
        if (!this.forecastFile) {
          await new Promise((resolve) => setTimeout(resolve, 800));
          await this.fetchForecastFile();
        }

        this.$bvToast.toast('Forecasting pivot report downloaded successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000
        });
      } catch (err) {
        console.error('Error generating forecasting report:', err);
        const errorMessage = err.response?.data?.message
          || err.message
          || 'Failed to generate forecasting report. Please try again.';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.generating = false;
      }
    },

    async fetchForecastFile() {
      try {
        const filesData = await ChatbotService.getUploads(this.candidateId, {
          type: 'forecast',
          page: 1,
          page_size: 10
        });
        const files = Array.isArray(filesData)
          ? filesData
          : (filesData.files || filesData.uploads || filesData.data || []);

        const latest = this.getLatestUpload(files);
        if (!latest) {
          this.forecastFile = null;
          return;
        }

        // Forecast uploads are generated Excel files and may not go through
        // the normal scan pipeline, so treat missing/pending as completed.
        const rawStatus = latest.scan_status || latest.status;
        this.forecastFile = {
          ...latest,
          status: (!rawStatus || rawStatus === 'pending') ? 'completed' : rawStatus
        };
      } catch (error) {
        console.error('Error fetching forecast file:', error);
      }
    },

    getLatestUpload(documents) {
      if (!Array.isArray(documents) || documents.length === 0) return null;

      return documents.reduce((latest, doc) => {
        const docDate = new Date(doc.uploaded_at || doc.scan_completed_at || 0);
        const latestDate = new Date(latest.uploaded_at || latest.scan_completed_at || 0);
        return docDate > latestDate ? doc : latest;
      });
    },

    formatUploadDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getDocumentStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },

    getDocumentStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing' || status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },

    getForecastFileId() {
      return this.forecastFile?.id || this.forecastFile?.file_id || null;
    },

    isReportDisabled(file) {
      if (!file) return true;

      const fileId = file.id || file.file_id;
      return this.downloadingReport === fileId
        || ['scanning', 'extracting', 'pending'].includes(file.status);
    },

    async downloadForecastFile(file) {
      const fileId = file?.id || file?.file_id;
      if (!fileId || this.isReportDisabled(file)) {
        return;
      }

      this.downloadingReport = fileId;

      try {
        const blob = await ChatbotService.downloadFile(fileId);

        if (!(blob instanceof Blob)) {
          throw new Error('Invalid file data received from server');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name || 'forecast.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Forecast report downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error downloading forecast file:', error);
        this.$bvToast.toast(
          `Error downloading report: ${error.message || 'Unknown error'}`,
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.downloadingReport = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import './index.scss';

// ── Page shell (matches analysis-pack / bank / vat layout) ───────────────────
.forecasting-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.forecasting-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
}

.forecasting-sections {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.forecasting-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.forecasting-section-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

// ── Inner sections (match kyb-section pattern) ────────────────────────────────
.f-section {
  background: #fbfcfe;
  border: 1px solid #eef1f6;
  border-radius: 16px;
  padding: 1.35rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.f-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.f-section-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #101828;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  font-family: $brand-font-bold;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 1.15rem;
    border-radius: 999px;
    background: linear-gradient(180deg, #0f9d58, #16a34a);
    flex-shrink: 0;
  }
}

// ── Loading / empty ───────────────────────────────────────────────────────────
.f-loading {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.f-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 0;
  text-align: center;
  color: #718096;
  font-family: $brand-font-regular;
  font-size: 0.9rem;

  p { margin: 0; }
}

.f-empty-icon {
  font-size: 2rem;
  color: #cbd5e0;
}

// ── Stress meta row ───────────────────────────────────────────────────────────
.stress-meta {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.stress-context {
  flex: 1;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.65;
  color: #4a5568;
  font-family: $brand-font-regular;
  min-width: 200px;
}

// ── Trend badge ───────────────────────────────────────────────────────────────
.trend-badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: $brand-font-semi-bold;
  white-space: nowrap;
  flex-shrink: 0;

  &.trend-growth      { background: #dcfce7; color: #15803d; }
  &.trend-decline     { background: #fee2e2; color: #dc2626; }
  &.trend-stable      { background: #dbeafe; color: #1d4ed8; }
  &.trend-mixed       { background: #fef3c7; color: #b45309; }
  &.trend-insufficient,
  &.trend-unknown     { background: #f1f5f9; color: #64748b; }
}

// ── Scenario cards ────────────────────────────────────────────────────────────
.f-hint {
  margin: 0;
  font-size: 0.78rem;
  color: #a0aec0;
  font-family: $brand-font-regular;
}

.scenario-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.scenario-card {
  background: #ffffff;
  border: 1.5px solid #e6e9f0;
  border-radius: 12px;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
  user-select: none;

  &:hover:not(.selected) {
    border-color: #c5cdd9;
    box-shadow: 0 2px 8px -3px rgba(16, 24, 40, 0.1);
  }

  &.selected {
    border-color: #0f9d58;
    box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.1);
    background: #f6fdf9;
  }

  &:focus-visible {
    border-color: #0f9d58;
    box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.2);
  }
}

.scenario-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.scenario-title-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.scenario-radio {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 2px solid #cbd5e0;
  flex-shrink: 0;
  position: relative;
  background: #fff;
  transition: border-color 0.15s;

  &.checked {
    border-color: #0f9d58;
    background: #fff;

    &::after {
      content: "";
      position: absolute;
      inset: 2px;
      border-radius: 50%;
      background: #0f9d58;
    }
  }
}

.scenario-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
  font-family: $brand-font-medium;
}

.scenario-input-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
}

.scenario-input {
  width: 72px;
  padding: 0.4rem 0.55rem;
  border: 1px solid #d3d9e4;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a202c;
  text-align: right;
  background: #fff;
  font-family: $brand-font-regular;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: #0f9d58;
    box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.14);
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  appearance: textfield;
  -moz-appearance: textfield;
}

.scenario-input-suffix {
  font-size: 0.8rem;
  color: #718096;
  font-family: $brand-font-medium;
}

.scenario-rationale {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.55;
  color: #718096;
  font-family: $brand-font-regular;
  flex: 1;
}

.scenario-payments {
  margin: 0;
  padding-top: 0.25rem;
  font-size: 0.775rem;
  color: #2d3748;
  font-family: $brand-font-medium;

  .scenario-card.selected & {
    color: #166534;
  }

  &.invisible { visibility: hidden; }
}

.scenario-payments-note {
  color: #a0aec0;
  font-weight: 400;
  font-family: $brand-font-regular;
}

// ── Cutoff date form (matches CandidateDetails organisation details) ─────────
.organisation-details-content {
  overflow: visible;

  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem 1.75rem;

    &.form-grid-single {
      grid-template-columns: minmax(0, 320px);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;

      &.form-grid-single {
        grid-template-columns: 1fr;
      }
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-label {
    position: relative;
    top: 5px;
    left: 5px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.045em;
    color: #667085;
    font-family: $brand-font-semi-bold;
  }

  .form-input {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d3d9e4;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    color: #121926;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #0f9d58;
      box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.16);
    }

    &::placeholder {
      color: #98a2b3;
      font-weight: 400;
    }

    &.date-input {
      cursor: pointer;
    }
  }

  .date-input-wrapper {
    position: relative;
    width: 100%;
    overflow: visible;
  }
}

.f-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.5rem;
}

.document-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.document-icon-large {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1.25rem;
    color: $color-primary;
  }
}

.document-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.document-date {
  font-size: 0.875rem;
  color: #6c757d;
  font-family: $brand-font-regular;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  background: transparent;

  &.status-completed {
    color: #16a34a;

    i {
      color: #16a34a;
    }
  }

  &.status-processing {
    color: #3b82f6;

    i {
      color: #3b82f6;
    }
  }

  &.status-error {
    color: #dc2626;

    i {
      color: #dc2626;
    }
  }

  &.status-pending {
    color: #6b7280;

    i {
      color: #6b7280;
    }
  }

  i {
    font-size: 1.3rem;
  }
}

.report-btn {
  background: transparent;
  border: none;
  color: #3182ce;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) i {
    color: darken(#3182ce, 10%);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  i {
    font-size: 1.3rem;
  }
}

.report-btn-spinner {
  margin-right: 0.2rem;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .forecasting-content { padding: 1rem 1rem 2rem; }
  .forecasting-section-header { align-items: flex-start; }
  .f-actions { justify-content: stretch; }
}
</style>
